import { ArrowRightOnRectangleIcon, BookmarkIcon, CreditCardIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { User } from 'lucide-react'
import { useNavigate } from 'react-router'

import { ISubscription } from '@/MODELS/subscription.model'

import { useAuth } from '@/services/authentication.service'
import { camelCaseToTitleCase } from '@/utils/form-utils'
import { monthOptions, servicesReceivedOptions } from '@/utils/options'

import Loading from '@/components/Loading'
import { SectionHeader } from '@/components/SectionHeader'


export default function UserProfile(props: { id?: string }) {
    const { user, fetchUser } = useAuth()
    const navigate = useNavigate()

    const getMemberSince = (date: Date | string) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
        })
    }

    if (!user) {
        return <Loading mode='block' text='Loading profile...' />
    }

    return (
        <div className='flex-1'>
            <SectionHeader
                title={'Profile'}
                subtitle={`Manage your personal information, regenerate roadmap, and update your account details`}
            />
            <div className='p-3 py-6 mx-auto '>
                <div className='p-6 mb-6 bg-white border border-gray-100 shadow-sm rounded-xl'>
                    <div className='flex items-center gap-4'>
                        <div className='flex items-center justify-center w-16 h-16 text-xl font-semibold text-white rounded-full bg-brand-600'>
                            <User />
                        </div>
                        <div>
                            <h1 className='text-xl font-semibold text-gray-900'>{user.name || 'User'}</h1>
                            <p className='text-sm text-gray-500'>Member since {getMemberSince(user.createdAt)}</p>
                            <p className='text-sm text-gray-500'>{user.email}</p>
                        </div>
                    </div>
                </div>

                {user.userInfo && (
                    <div className='mb-6'>
                        <h2 className='mb-4 text-lg font-semibold text-gray-900'>Child Information</h2>

                        <div className='p-6 bg-white border border-gray-200 shadow-sm rounded-xl'>
                            <div className='grid grid-cols-2 gap-6 mb-6'>
                                <div>
                                    <p className='mb-1 text-sm text-gray-500'>
                                        {user.userInfo.childBirthMonth && user.userInfo.childBirthYear ? 'Date of Birth' : 'Age Range'}
                                    </p>
                                    <p className='text-sm font-medium text-gray-900'>
                                        {user.userInfo.childBirthMonth && user.userInfo.childBirthYear
                                            ? `${monthOptions.find((m) => m.value === String(user.userInfo.childBirthMonth))?.text ?? ''} ${user.userInfo.childBirthYear}`
                                            : user.userInfo.childAge
                                              ? `${user.userInfo.childAge} years`
                                              : 'Not specified'}
                                    </p>
                                </div>
                                <div>
                                    <p className='mb-1 text-sm text-gray-500'>Current Status</p>
                                    <p className='text-sm font-medium text-gray-900'>
                                        {user.userInfo.currentDiagnoseStep}
                                    </p>
                                </div>
                            </div>

                            <div className='mb-4'>
                                <p className='mb-2 text-sm text-gray-500'>Areas of Focus</p>

                                {user.userInfo.mainConcerns.map((con) => {
                                    return (
                                        <div className='flex flex-wrap gap-2'>
                                            <span className='px-3 py-1 text-sm rounded-full text-brand-700 bg-blue-50'>
                                                {con.charAt(0).toUpperCase() + con.slice(1)}
                                            </span>
                                        </div>
                                    )
                                })}
                            </div>

                            {user.userInfo.servicesReceivedConsent && (user.userInfo.servicesReceived?.length ?? 0) > 0 && (
                                <div className='mb-4'>
                                    <p className='mb-2 text-sm text-gray-500'>Services Currently Received</p>
                                    <div className='flex flex-wrap gap-2'>
                                        {user.userInfo.servicesReceived!.map((s) => (
                                            <span key={s} className='px-3 py-1 text-sm rounded-full text-brand-700 bg-blue-50'>
                                                {servicesReceivedOptions.find((opt) => opt.value === s)?.text ?? s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <span
                                onClick={() => navigate(`/manage-profile`)}
                                className='text-sm text-brand-600 hover:text-brand-700 cursor-pointer'
                            >
                                Update Information
                            </span>
                        </div>
                    </div>
                )}

                <div>
                    <h2 className='mb-4 text-lg font-semibold text-gray-900'>Settings</h2>

                    <div className='space-y-3'>
                        <button
                            onClick={() => navigate('/saved-resources')}
                            className='flex items-center justify-between w-full p-4 transition-colors bg-white border border-gray-200 shadow-sm rounded-xl hover:bg-gray-50'
                        >
                            <div className='flex items-center gap-3'>
                                <BookmarkIcon className='w-5 h-5 text-gray-400' />
                                <span className='text-sm font-medium text-gray-900'>Saved Resources</span>
                            </div>
                        </button>

                        <button
                            onClick={() => navigate('/manage-subscription')}
                            className='flex items-center justify-between w-full p-4 transition-colors bg-white border border-gray-200 shadow-sm rounded-xl hover:bg-gray-50'
                        >
                            <div className='flex items-center gap-3'>
                                <CreditCardIcon className='w-5 h-5 text-gray-400' />
                                <span className='text-sm font-medium text-gray-900'>Manage Subscription</span>
                            </div>
                        </button>

                        <button
                            onClick={() => navigate('/privacy-policy')}
                            className='flex items-center justify-between w-full p-4 transition-colors bg-white border border-gray-200 shadow-sm rounded-xl hover:bg-gray-50'
                        >
                            <div className='flex items-center gap-3'>
                                <ShieldCheckIcon className='w-5 h-5 text-gray-400' />
                                <span className='text-sm font-medium text-gray-900'>Privacy & Data</span>
                            </div>
                        </button>

                        <button
                            onClick={() => navigate('/logout')}
                            className='flex items-center justify-center w-full p-4 mt-6 transition-colors bg-white border border-red-200 shadow-sm rounded-xl hover:bg-red-50'
                        >
                            <div className='flex items-center gap-3'>
                                <ArrowRightOnRectangleIcon className='w-5 h-5 text-red-600' />
                                <span className='text-sm font-medium text-red-600'>Sign Out</span>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export const StripeSubscriptionBadge = (props: { subscription: ISubscription }) => {
    const colors = {
        active: 'bg-green-50 text-green-700 ring-green-600/20',
        trialing: 'bg-green-50 text-green-700 ring-green-600/20',
        paused: 'bg-yellow-50 text-yellow-800 ring-yellow-600/20',
        canceled: 'bg-red-50 text-red-700 ring-red-600/10',
        incomplete: 'bg-red-50 text-red-700 ring-red-600/10',
        incomplete_expired: 'bg-red-50 text-red-700 ring-red-600/10',
        unpaid: 'bg-red-50 text-red-700 ring-red-600/10',
        past_due: 'bg-red-50 text-red-700 ring-red-600/10',
    }

    return (
        <div
            className={`inline-flex items-center px-2 py-1 text-sm font-medium rounded-md ring-1 ring-inset ${colors[props?.subscription?.subscriptionStatus ?? 'canceled']}`}
        >
            {camelCaseToTitleCase(props?.subscription?.subscriptionStatus ?? 'Not Subscribed')}
        </div>
    )
}
